import * as React from 'react';

export interface CompletionPoint {
  label: string;
  planned: number;
  completed: number;
}

interface CompletionTrendProps {
  points: CompletionPoint[];
  completionRate?: number;
  avgPerDay?: number;
}

/**
 * CompletionTrend — daily "planned vs completed" delivery, drawn as paired
 * CSS-height bars (crisp labels, no SVG scaling). Sourced from the dispatch
 * board insights aggregator's `completion[]`.
 */
export function CompletionTrend({
  points,
  completionRate,
  avgPerDay,
}: CompletionTrendProps) {
  const max = Math.max(1, ...points.flatMap((p) => [p.planned, p.completed]));

  const hasData = points.some((p) => p.planned > 0 || p.completed > 0);

  return (
    <div>
      <div className="tw-mb-3 tw-flex tw-gap-4 tw-text-[11.5px] tw-text-muted-foreground">
        <span className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-bg-muted-foreground/30" />
          Planned
        </span>
        <span className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-bg-success" />
          Completed
        </span>
      </div>

      {hasData ? (
        <div className="tw-flex tw-h-[150px] tw-items-end tw-justify-between tw-gap-2">
          {points.map((p, i) => (
            <div
              key={`${p.label}-${i}`}
              className="tw-flex tw-h-full tw-flex-1 tw-flex-col tw-items-center tw-justify-end tw-gap-1"
              title={`${p.label}: ${p.completed} of ${p.planned} completed`}
            >
              <div className="tw-flex tw-h-full tw-w-full tw-items-end tw-justify-center tw-gap-[3px]">
                <div
                  className="tw-w-1/2 tw-max-w-[16px] tw-rounded-t-sm tw-bg-muted-foreground/25"
                  style={{ height: `${(p.planned / max) * 100}%` }}
                />
                <div
                  className="tw-w-1/2 tw-max-w-[16px] tw-rounded-t-sm tw-bg-success"
                  style={{ height: `${(p.completed / max) * 100}%` }}
                />
              </div>
              <span className="tw-text-[10px] tw-text-muted-foreground">{p.label}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="tw-flex tw-h-[150px] tw-items-center tw-justify-center tw-text-sm tw-text-muted-foreground">
          No activity scheduled in this period.
        </p>
      )}

      {(completionRate !== undefined || avgPerDay !== undefined) && (
        <div className="tw-mt-3 tw-flex tw-gap-5 tw-text-[11.5px] tw-text-muted-foreground">
          {completionRate !== undefined ? (
            <span>
              Completion rate{' '}
              <b className="tw-text-success tw-tabular-nums">
                {Math.round(completionRate)}%
              </b>
            </span>
          ) : null}
          {avgPerDay !== undefined ? (
            <span>
              Avg activities / day{' '}
              <b className="tw-tabular-nums tw-text-foreground">{avgPerDay}</b>
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
